import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import { postUserPermission, updateUserPermission } from "api/graphQL_role";
import React from "react";
import { toast } from "react-hot-toast";
import { MdEdit, MdHighlightOff, MdInfo } from "react-icons/md";
import Checkbox from "components/checkbox";
import DivLoader from "components/divloader/DivLoader";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";

/* ── Permission section config ── */
const SECTIONS = [
  { id: "super_admin", label: "Super Admin", icon: "shield", color: "red",
    data: ["Super admin view"] },
  { id: "dashboard", label: "Dashboard", icon: "dashboard", color: "blue",
    data: ["View dashboard header","View dashboard recent transactions","View dashboard approved transactions piechart","View dashboard declined transactions piechart","View dashboard approved transactions list","View dashboard declined transactions list","View dashboard wavechart","View dashboard transaction anaylsis","Show dashboard method name"] },
  { id: "client", label: "Client", icon: "client", color: "green",
    data: ["View client list","Add client","View client","Edit client","Delete client","Client checkout setting","Client gateway settings","Client gateway routing","Import smart route json","Client show method name","Client gateway pricing","Client gateway settlement","View client treasury","Add client treasury","Customer Registration","Assign Client Gateway"] },
  { id: "org", label: "Organization", icon: "org", color: "blue",
    data: ["View organization list","Add organization","View organization","Edit organization","Delete organization","Organization show method name"] },
  { id: "pay", label: "Transactions", icon: "payment", color: "amber",
    data: ["View payment list","Refund payment list","Retry payment list","View payment response information","View payment receipt","Payment show method name","Edit payment status"] },
  { id: "user", label: "User", icon: "user", color: "green",
    data: ["View self created user","View user list","Add user","View user","Edit user","Delete user"] },
  { id: "paymeth", label: "Gateways", icon: "gateway", color: "purple",
    data: ["View payment method list","Edit payment method","Show payment method name"] },
  { id: "role", label: "Role", icon: "role", color: "red",
    data: ["View role list","Add role","Edit role","Delete role","View role","View super admin role permission"] },
  { id: "payout", label: "Payout", icon: "payout", color: "amber",
    data: ["View payout list","View payout response information","Payout show method name","Refund payout list","Retry payout list","View payout receipt","Edit payout status","View batch payout","Add payout batch file"] },
  { id: "subscription", label: "Subscription", icon: "subs", color: "blue",
    data: ["View Subscription list","Subscription show method name","Cancel Subscription","View subscription response information"] },
  { id: "settlement", label: "Settlement", icon: "settlement", color: "green",
    data: ["View settlement list","Generate settlement report","Edit settlement","Delete settlement","Approve settlement","Download settlement"] },
  { id: "blacklist", label: "Black List", icon: "blacklist", color: "red",
    data: ["View BlackList List","Add BlackList","Delete BlackList","Edit BlackList","View BlackList"] },
  { id: "whitelist", label: "White List", icon: "whitelist", color: "green",
    data: ["View WhiteList List","Add WhiteList","Delete WhiteList","Edit WhiteList","View WhiteList"] },
  { id: "api", label: "API Access", icon: "api", color: "purple",
    data: ["View api list","Add api","Edit api","View api","View history api","Block api","Delete api"] },
  { id: "settings", label: "Settings", icon: "settings", color: "amber",
    data: ["View Settings","Update Settings"] },
  { id: "customers", label: "Customers", icon: "customers", color: "blue",
    data: ["View Customers","Upload Kyc Document","View Kyc Document","Add Customer","Edit Customer","Delete Customer","Show Customer Gateway Name"] },
  { id: "api_request_logs", label: "API Request Logs", icon: "logs", color: "purple",
    data: ["View Api Request Logs"] },
  { id: "crypto", label: "Crypto", icon: "crypto", color: "amber",
    data: ["View Crypto Payout List","Add Batch File","Approve Batch File","View Crypto Payin","Add Crypto Payin Batch File","View crypto payout response information","View single crypto payout list","Show crypto payout gateway name"] },
  { id: "kyc_velocity", label: "KYC Velocity", icon: "kyc", color: "green",
    data: ["View Kyc Velocity","Add Kyc Velocity","Update Kyc Velocity","Delete Kyc Velocity","View Kyc Velocity Details"] },
  { id: "fiat", label: "Fiat", icon: "fiat", color: "blue",
    data: ["View Fiat Payout List","Add Fiat Batch File","Approve Fiat Batch File","View fiat payout response information","View single fiat payout list","Show fiat payout gateway name"] },
  { id: "audit_logs", label: "Audit Logs", icon: "audit", color: "purple",
    data: ["View User Audit Logs"] },
];

const COLOR_MAP: Record<string, { header: string; dot: string; check: string }> = {
  blue:   { header: "linear-gradient(135deg,#eff6ff,#dbeafe)", dot: "#bfdbfe", check: "#2563eb" },
  green:  { header: "linear-gradient(135deg,#f0fdf4,#dcfce7)", dot: "#bbf7d0", check: "#16a34a" },
  amber:  { header: "linear-gradient(135deg,#fffbeb,#fef3c7)", dot: "#fde68a", check: "#d97706" },
  purple: { header: "linear-gradient(135deg,#faf5ff,#ede9fe)", dot: "#ddd6fe", check: "#7c3aed" },
  red:    { header: "linear-gradient(135deg,#fff1f2,#ffe4e6)", dot: "#fecdd3", check: "#e11d48" },
};

const ErrorMsg = ({ msg }: { msg?: string }) => msg ? (
  <p className="crm-err">
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
    {msg}
  </p>
) : null;

const CreateRoleMethod = ({
  fetchRole, fetchSingleRole, id, is_info, data, singleRoleData,
}: {
  fetchRole: () => void;
  fetchSingleRole: () => void;
  id?: string;
  is_info?: boolean;
  data?: any;
  singleRoleData?: any;
}) => {
  const rootForm = { name: "" };
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [formValues, setFormValues] = React.useState<any>(rootForm);
  const [viewMode, setViewMode] = React.useState<boolean>(is_info);
  const [formValuesErr, setFormValuesErr] = React.useState<any>({});
  const [isCloseModal, setIsCloseModal] = React.useState<boolean>(false);
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const [popoverOpenE, setPopoverOpenE] = React.useState(false);

  /* ── Per-section selection state ── */
  const [sel, setSel] = React.useState<Record<string, string[]>>({});

  const getSel = (id: string) => sel[id] || [];
  const setSec = (id: string, val: string[]) => setSel((p) => ({ ...p, [id]: val }));

  const isSuperAdmin = getSel("super_admin").includes("Super admin view");

  const handleClose = () => {
    setFormValues(rootForm);
    setFormValuesErr({});
    setViewMode(is_info);
    setPopoverOpenE(false)
    setPopoverOpen(false)
    setSel({});
    setIsCloseModal(true);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  const handleChange = (event: any) => {
    const { value, id: fid } = event.target;
    const section = SECTIONS.find((s) => s.id === fid);
    if (!section) return;
    const current = getSel(fid);
    if (value === "all") {
      setSec(fid, current.length === section.data.length ? [] : [...section.data]);
    } else if (value === "-1") {
      setSec(fid, []);
    } else {
      setSec(fid, current.includes(value) ? current.filter((d) => d !== value) : [...current, value]);
    }
  };

  function validateData() {
    let verifier: any = {};
    if (!formValues.name?.trim()) verifier.name = "Role name is required";
    setFormValuesErr(verifier);
    return Object.keys(verifier).length === 0;
  }

  const buildPayload = () => {
    const sa = isSuperAdmin;
    const has = (secId: string, perm: string) => getSel(secId).includes(perm) || sa;

    return {
      name: formValues?.name?.toUpperCase()?.trim(),
      super_admin: { super_admin_view: isSuperAdmin },
      dashboard: {
        view_dashboard_header: has("dashboard","View dashboard header"),
        view_dashboard_recent_transactions: has("dashboard","View dashboard recent transactions"),
        view_dashboard_approved_transactions_piechart: has("dashboard","View dashboard approved transactions piechart"),
        view_dashboard_declined_transactions_piechart: has("dashboard","View dashboard declined transactions piechart"),
        view_dashboard_approved_transactions_list: has("dashboard","View dashboard approved transactions list"),
        view_dashboard_declined_transactions_list: has("dashboard","View dashboard declined transactions list"),
        view_dashboard_wavechart: has("dashboard","View dashboard wavechart"),
        view_dashboard_transaction_anaylsis: has("dashboard","View dashboard transaction anaylsis"),
        show_dashboard_method_name: has("dashboard","Show dashboard method name"),
      },
      user: {
        view_user_list: has("user","View user list"),
        view_self_created_user: has("user","View self created user"),
        add_user: has("user","Add user"),
        view_user: has("user","View user"),
        edit_user: has("user","Edit user"),
        delete_user: has("user","Delete user"),
      },
      payment: {
        view_payment_list: has("pay","View payment list"),
        refund_payment_list: has("pay","Refund payment list"),
        retry_payment_list: has("pay","Retry payment list"),
        view_payment_response_information: has("pay","View payment response information"),
        view_payment_receipt: has("pay","View payment receipt"),
        payment_show_method_name: has("pay","Payment show method name"),
        edit_payment_status: has("pay","Edit payment status"),
      },
      token: { view_token_list: false, token_show_method_name: false },
      payout: {
        view_payout_list: has("payout","View payout list"),
        view_payout_response_information: has("payout","View payout response information"),
        payout_show_method_name: has("payout","Payout show method name"),
        refund_payout_list: has("payout","Refund payout list"),
        retry_payout_list: has("payout","Retry payout list"),
        view_payout_receipt: has("payout","View payout receipt"),
        edit_payout_status: has("payout","Edit payout status"),
        view_batch_payout: has("payout","View batch payout"),
        add_payout_batch_file: has("payout","Add payout batch file"),
      },
      organization: {
        view_organization_list: has("org","View organization list"),
        add_organization: has("org","Add organization"),
        view_organization: has("org","View organization"),
        edit_organization: has("org","Edit organization"),
        delete_organization: has("org","Delete organization"),
        organization_show_method_name: has("org","Organization show method name"),
      },
      client: {
        view_client_list: has("client","View client list"),
        add_client: has("client","Add client"),
        view_client: has("client","View client"),
        edit_client: has("client","Edit client"),
        delete_client: has("client","Delete client"),
        client_checkout_setting: has("client","Client checkout setting"),
        client_gateway_settings: has("client","Client gateway settings"),
        client_gateway_routing: has("client","Client gateway routing"),
        import_smart_route_json: has("client","Import smart route json"),
        client_show_method_name: has("client","Client show method name"),
        client_gateway_pricing: has("client","Client gateway pricing"),
        client_gateway_settlement: has("client","Client gateway settlement"),
        view_client_treasury: has("client","View client treasury"),
        add_client_treasury: has("client","Add client treasury"),
        customer_registration: has("client","Customer Registration"),
        assign_client_gateway: has("client","Assign Client Gateway"),
      },
      role: {
        view_role_list: has("role","View role list"),
        add_role: has("role","Add role"),
        edit_role: has("role","Edit role"),
        delete_role: has("role","Delete role"),
        view_role: has("role","View role"),
        view_super_admin_role_permission: has("role","View super admin role permission"),
      },
      payment_method: {
        view_payment_method_list: has("paymeth","View payment method list"),
        edit_payment_method: has("paymeth","Edit payment method"),
        show_payment_method_name: has("paymeth","Show payment method name"),
      },
      subscription: {
        view_subscription_list: has("subscription","View Subscription list"),
        subscription_show_method_name: has("subscription","Subscription show method name"),
        cancel_subscription: has("subscription","Cancel Subscription"),
        view_subscription_response_information: has("subscription","View subscription response information"),
      },
      blacklist: {
        view_blacklist_list: has("blacklist","View BlackList List"),
        delete_blacklist: has("blacklist","Delete BlackList"),
        edit_blacklist: has("blacklist","Edit BlackList"),
        add_blacklist: has("blacklist","Add BlackList"),
        view_blacklist: has("blacklist","View BlackList"),
      },
      whitelist: {
        view_whitelist_list: has("whitelist","View WhiteList List"),
        delete_whitelist: has("whitelist","Delete WhiteList"),
        edit_whitelist: has("whitelist","Edit WhiteList"),
        add_whitelist: has("whitelist","Add WhiteList"),
        view_whitelist: has("whitelist","View WhiteList"),
      },
      settlement: {
        view_settlement_list: has("settlement","View settlement list"),
        generate_settlement_report: has("settlement","Generate settlement report"),
        edit_settlement: has("settlement","Edit settlement"),
        delete_settlement: has("settlement","Delete settlement"),
        download_settlement: has("settlement","Download settlement"),
        approve_settlement: has("settlement","Approve settlement"),
      },
      api: {
        view_api_list: has("api","View api list"),
        add_api: has("api","Add api"),
        edit_api: has("api","Edit api"),
        view_api: has("api","View api"),
        view_history_api: has("api","View history api"),
        block_api: has("api","Block api"),
        delete_api: has("api","Delete api"),
      },
      settings: {
        view_settings: has("settings","View Settings"),
        update_settings: has("settings","Update Settings"),
      },
      customers: {
        view_customers: has("customers","View Customers"),
        upload_kyc_document: has("customers","Upload Kyc Document"),
        view_kyc_document: has("customers","View Kyc Document"),
        add_customer: has("customers","Add Customer"),
        edit_customer: has("customers","Edit Customer"),
        delete_customer: has("customers","Delete Customer"),
        show_customer_gateway_name: has("customers","Show Customer Gateway Name"),
      },
      api_request_logs: { view_api_request_logs: has("api_request_logs","View Api Request Logs") },
      crypto: {
        view_crypto_payout_list: has("crypto","View Crypto Payout List"),
        add_batch_file: has("crypto","Add Batch File"),
        approve_batch_file: has("crypto","Approve Batch File"),
        view_crypto_payin: has("crypto","View Crypto Payin"),
        add_crypto_payin_batch_file: has("crypto","Add Crypto Payin Batch File"),
        view_crypto_payout_response_information: has("crypto","View crypto payout response information"),
        view_single_crypto_payout_list: has("crypto","View single crypto payout list"),
        show_crypto_payout_gateway_name: has("crypto","Show crypto payout gateway name"),
      },
      kyc_velocity: {
        view_kyc_velocity: has("kyc_velocity","View Kyc Velocity"),
        add_kyc_velocity: has("kyc_velocity","Add Kyc Velocity"),
        update_kyc_velocity: has("kyc_velocity","Update Kyc Velocity"),
        delete_kyc_velocity: has("kyc_velocity","Delete Kyc Velocity"),
        view_kyc_velocity_details: has("kyc_velocity","View Kyc Velocity Details"),
      },
      fiat: {
        view_fiat_payout_list: has("fiat","View Fiat Payout List"),
        add_fiat_batch_file: has("fiat","Add Fiat Batch File"),
        approve_fiat_batch_file: has("fiat","Approve Fiat Batch File"),
        view_fiat_payout_response_information: has("fiat","View fiat payout response information"),
        view_single_fiat_payout_list: has("fiat","View single fiat payout list"),
        show_fiat_payout_gateway_name: has("fiat","Show fiat payout gateway name"),
      },
      audit_logs: { view_user_audit_logs: has("audit_logs","View User Audit Logs") },
    };
  };

  async function handleSubmit(e: any) {
    e.preventDefault();
    if (!validateData()) return;
    try {
      setIsLoading(true);
      const payload = buildPayload();
      if (id) {
        const update = await updateUserPermission(payload, id);
        if (update.errors) { toast.error(update.errors[0].message); }
        else { fetchRole(); fetchSingleRole(); handleClose(); toast.success("Role updated successfully"); }
      } else {
        const create = await postUserPermission(payload);
        if (create.errors) { toast.error(create.errors[0].message); }
        else { fetchRole(); handleClose(); toast.success("Role created successfully"); }
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Unable to save role");
    } finally {
      setIsLoading(false);
    }
  }

  const initForm = () => {
    if (!data) return;
    setFormValues({ name: data?.role_name });
    const newSel: Record<string, string[]> = {};
    SECTIONS.forEach((sec) => {
      const moduleKey = sec.id === "pay" ? "payment" : sec.id === "org" ? "organization" : sec.id === "paymeth" ? "payment_method" : sec.id;
      const moduleData = data?.[moduleKey]?.value || {};
      newSel[sec.id] = sec.data.filter((item) => {
        const key = item.toLowerCase().replace(/ /g, "_");
        return moduleData[key];
      });
    });
    setSel(newSel);
  };

  /* ── Trigger ── */
  const TriggerEl = () => {
    if (is_info) {
      if (!singleRoleData?.[0]?.role?.value?.view_role) return null;
      return (
        <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpen && !isOpen} onOpen={() => setPopoverOpen(true)} onClose={() => setPopoverOpen(false)}>
          <PopoverTrigger>
            <button onClick={() => { if (data) initForm(); setViewMode(true); onOpen(); }} onMouseEnter={() => !isOpen && setPopoverOpen(true)} onMouseLeave={() => setPopoverOpen(false)}>
              <div className="rl-action-icon rl-action-icon--blue"><MdInfo size={16}/></div>
            </button>
          </PopoverTrigger>
          <PopoverContent border="1px solid #bfdbfe" borderRadius="12px" bg="#eff6ff" maxW="180px" p="0" overflow="hidden" zIndex="20">
            <PopoverHeader bg="#dbeafe" fontSize="12px" fontWeight="700" color="#1e40af" borderBottom="1px solid #bfdbfe" px="12px" py="9px">View Role</PopoverHeader>
            <PopoverArrow bg="#eff6ff"/>
          </PopoverContent>
        </Popover>
      );
    }
    if (id) {
      if (!singleRoleData?.[0]?.role?.value?.edit_role) return null;
      return (
        <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpenE && !isOpen} onOpen={() => setPopoverOpenE(true)} onClose={() => setPopoverOpenE(false)}>
          <PopoverTrigger>
            <button onClick={() => { if (data) initForm(); setViewMode(false); onOpen(); }} onMouseEnter={() => !isOpen && setPopoverOpenE(true)} onMouseLeave={() => setPopoverOpenE(false)}>
              <div className="rl-action-icon rl-action-icon--amber"><MdEdit size={16}/></div>
            </button>
          </PopoverTrigger>
          <PopoverContent border="1px solid #fde68a" borderRadius="12px" bg="#fffbeb" maxW="180px" p="0" overflow="hidden" zIndex="20">
            <PopoverHeader bg="#fef3c7" fontSize="12px" fontWeight="700" color="#92400e" borderBottom="1px solid #fde68a" px="12px" py="9px">Edit Role</PopoverHeader>
            <PopoverArrow bg="#fffbeb"/>
          </PopoverContent>
        </Popover>
      );
    }
    if (!singleRoleData?.[0]?.role?.value?.add_role) return null;
    return (
      <button className="rl-add-btn" onClick={() => { setViewMode(false); setSel({}); onOpen(); }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
        </svg>
        Create Role
      </button>
    );
  };

  /* ── Render a permission section card ── */
  const renderSection = (sec: typeof SECTIONS[0]) => {
    const current = getSel(sec.id);
    const isAll = sec.data.length > 0 && current.length === sec.data.length;
    const col = COLOR_MAP[sec.color];
    const isSuper = sec.id === "super_admin";

    return (
      <div key={sec.id} className="crm-perm-card">
        <div className="crm-perm-header" style={{ background: col.header, borderColor: col.dot }}>
          <Checkbox
            id={sec.id}
            value={isAll ? "-1" : "all"}
            checked={isAll}
            onChange={handleChange}
            disabled={viewMode}
            color={viewMode ? "gray" : ""}
          />
          <span className="crm-perm-title" style={{ color: col.check }}>{sec.label}</span>
          {current.length > 0 && (
            <span className="crm-perm-count" style={{ background: col.dot, color: col.check }}>
              {current.length}/{sec.data.length}
            </span>
          )}
        </div>
        <div className="crm-perm-body">
          {sec.data.map((item) => (
            <label key={item} className="crm-perm-row">
              <Checkbox
                id={sec.id}
                value={item}
                checked={current.includes(item)}
                onChange={handleChange}
                disabled={viewMode}
                color={viewMode ? "gray" : ""}
              />
              <span className="crm-perm-label">
                <span className="crm-perm-label">
                  {sec.label === "Dashboard" ? (

                    item === "Show dashboard method name" ? "Show gateway name".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "View dashboard header" ? "View transaction count".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "View dashboard transaction anaylsis" ? "View Transaction Analysis".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item.replace("dashboard", "").replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                  
                  ) : sec.label === "Client" ? (

                    item === "Client show method name" ? "Show gateway name".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "Assign Client Gateway" ? "Assign Gateway".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())

                  ) : sec.label === "Organization" ? (

                    item === "Organization show method name" ? "Show gateway name".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())

                  ) : sec.label === "Transactions" ? (

                    item === "Payment show method name" ? "Show gateway name".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "View payment list" ? "View transaction list".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "Refund payment list" ? "Refund transaction list".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "Retry payment list" ? "Retry transaction list".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "Edit payment status" ? "Edit transaction status".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "View advanced response" ? "View advanced response".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())

                  ) :  sec.label === "User" ? (

                    item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())

                  ) : sec.label === "Gateways" ? (

                    item === "View payment method list" ? "View gateway list".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "Show payment method name" ? "Show gateway name".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "Edit payment method" ? "Edit gateway".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                  
                  ) : sec.label === "Role" ? (

                    item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())

                  ) : sec.label === "Payout" ? (

                    item === "Payout show method name" ? "Show gateway name".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    
                  ) : sec.label === "Subscription" ? (

                    item === "Subscription show method name" ? "Show gateway name".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    
                  ) : sec.label === "Black List" ? (

                    item === "View BlackList" ? "View History".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    
                  ) : sec.label === "White List" ? (

                    item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    
                  ) : sec.label === "Settlement" ? (

                    item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    
                  ) : sec.label === "API Access" ? (

                    item === "View api list" ? "View api access list".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "Add api" ? "Add api access".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "Edit api" ? "Edit api access".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "View api" ? "View api access".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "View history api" ? "View history api access".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "Block api" ? "Block api access".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "Delete api" ? "Delete api access".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    
                  ) : sec.label === "Settings" ? (

                    item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    
                  ) : sec.label === "Customers" ? (

                    item === "Show Customer Gateway Name" ? "Show gateway name".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    
                  ) : sec.label === "API Request Logs" ? (

                    item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    
                  ) : sec.label === "Crypto" ? (

                    item === "View Crypto Payout List" ? "View Batch Crypto Payout List".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "Show crypto payout gateway name" ? "Show Gateway Name".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    
                  ) : sec.label === "Fiat" ? (

                    item === "View Fiat Payout List" ? "View Batch Fiat Payout List".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item === "Show fiat payout gateway name" ? "Show Gateway Name".replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    : item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    
                  ) : sec.label === "KYC Velocity" ? (

                    item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    
                  ) : sec.label === "Audit Logs" ? (

                    item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    
                  ) : sec.label === "Super Admin" ? (

                    item?.replace(/(^\w{1})|(\s+\w{1})/g,(letter) => letter.toUpperCase())
                    
                  ) : null}
                </span>
              </span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const superAdminSection = SECTIONS.find((s) => s.id === "super_admin");
  const otherSections = SECTIONS.filter((s) => s.id !== "super_admin");

  return (
    <>
      <TriggerEl/>

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)"/>

        <div className={`crm-panel${isOpen && !isCloseModal ? " crm-panel--open" : " crm-panel--close"}`}>

          <div className="crm-top-glow"/>

          {/* Header */}
          <div className="crm-header">
            <div className="crm-header-brand">
              <div className="crm-header-logo">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <div>
                <h2 className="crm-header-title">
                  {viewMode ? "Role Details" : id ? `Edit — ${data?.role_name ?? "Role"}` : "Create Role"}
                </h2>
                <p className="crm-header-sub">Configure module-level permissions</p>
              </div>
            </div>
            <div className="crm-header-right">
              {viewMode && singleRoleData?.[0]?.role?.value?.edit_role && (
                <button className="crm-edit-btn" onClick={() => setViewMode(false)}>
                  <MdEdit size={13}/> Switch to Edit
                </button>
              )}
              <button className="crm-close-btn" onClick={handleClose} aria-label="Close">
                <MdHighlightOff size={22}/>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="crm-body">
            {/* Role name */}
            <div className="crm-name-wrap">
              <label className="crm-label">Role Name <span className="crm-req">*</span></label>
              <div className="crm-input-icon-wrap">
                <span className="crm-input-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                </span>
                <input
                  type="text"
                  className={`crm-input${formValuesErr?.name ? " crm-input--err" : ""}`}
                  placeholder="e.g. ADMIN"
                  disabled={viewMode}
                  value={formValues?.name ?? ""}
                  onChange={(e) => {
                    setFormValues((p: any) => ({ ...p, name: e.target.value }));
                    setFormValuesErr((p: any) => ({ ...p, name: "" }));
                  }}
                />
              </div>
              <ErrorMsg msg={formValuesErr?.name}/>
            </div>

            {/* Super admin — full width if permitted */}
            {singleRoleData?.[0]?.role?.value?.view_super_admin_role_permission && superAdminSection && (
              <div className="crm-super-wrap">
                {renderSection(superAdminSection)}
              </div>
            )}

            {/* Permission grid */}
            <div className="crm-perm-grid">
              {otherSections.map(renderSection)}
            </div>
          </div>

          {/* Footer */}
          <div className="crm-footer">
            <span className="crm-footer-hint">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              Changes apply immediately on save
            </span>
            <div className="crm-footer-btns">
              <button className="crm-btn-cancel" onClick={handleClose}>Cancel</button>
              {!viewMode && (
                <button className="crm-btn-submit" onClick={handleSubmit} disabled={isLoading}>
                  {isLoading ? <span className="crm-spinner"/> : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      {id ? "Update Role" : "Create Role"}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        /* Trigger */
        .rl-add-btn {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 20px; border-radius: 10px; border: none;
          background: linear-gradient(135deg,#be123c,#e11d48);
          cursor: pointer; box-shadow: 0 3px 12px rgba(225,29,72,.28);
          transition: transform .15s, box-shadow .18s;
        }
        .rl-add-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(225,29,72,.36); }

        .rl-action-icon {
          width: 32px; height: 32px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all .15s; border: 1.5px solid;
        }
        .rl-action-icon--blue { background: #eff6ff; border-color: #bfdbfe; color: #2563eb; }
        .rl-action-icon--blue:hover { background: #dbeafe; }
        .rl-action-icon--amber { background: #fffbeb; border-color: #fde68a; color: #d97706; }
        .rl-action-icon--amber:hover { background: #fef3c7; }

        /* Panel */
        .crm-panel {
          font-family: 'Plus Jakarta Sans', sans-serif;
          position: fixed; top: 0; right: 0; bottom: 0;
          width: 100%; max-width: 900px;
          background: #fff; z-index: 1004;
          display: flex; flex-direction: column;
          box-shadow: -12px 0 60px rgba(15,23,42,.18);
          transition: transform .35s cubic-bezier(0.16,1,0.3,1);
          overflow: hidden;
        }
        .crm-panel--open  { transform: translateX(0); }
        .crm-panel--close { transform: translateX(100%); }

        .crm-top-glow {
          height: 3px; flex-shrink: 0;
          background: linear-gradient(90deg,#7f1d1d,#e11d48,#f97316,#e11d48,#7f1d1d);
          background-size: 200% 100%;
          animation: crmGlow 3s linear infinite;
        }
        @keyframes crmGlow { 0%{background-position:0%} 100%{background-position:200%} }

        /* Header */
        .crm-header {
          padding: 15px 26px;
          background: linear-gradient(135deg,#7f1d1d 0%,#9f1239 60%,#e11d48 100%);
          display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;
        }
        .crm-header-brand { display: flex; align-items: center; gap: 13px; }
        .crm-header-logo {
          width: 42px; height: 42px; border-radius: 12px;
          background: rgba(255,255,255,.15); border: 1.5px solid rgba(255,255,255,.25);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .crm-header-title { font-size: 16px; font-weight: 800; color: #fff; letter-spacing: -.3px; margin-bottom: 2px; line-height: 1; }
        .crm-header-sub { font-size: 12px; color: rgba(255,255,255,.55); font-weight: 500; }
        .crm-header-right { display: flex; align-items: center; gap: 10px; }
        .crm-edit-btn {
          display: inline-flex; align-items: center; gap: 5px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 12px; font-weight: 700; color: #fff;
          background: rgba(255,255,255,.14); border: 1.5px solid rgba(255,255,255,.25);
          border-radius: 8px; padding: 7px 12px; cursor: pointer; transition: background .15s;
        }
        .crm-edit-btn:hover { background: rgba(255,255,255,.24); }
        .crm-close-btn {
          background: none; border: none; cursor: pointer;
          color: rgba(255,255,255,.65); display: flex; align-items: center; padding: 2px; transition: color .15s;
        }
        .crm-close-btn:hover { color: #fff; }

        /* Body */
        .crm-body { flex: 1; overflow-y: auto; overflow-x: hidden; padding: 24px 28px; display: flex; flex-direction: column; gap: 20px; }
        .crm-body::-webkit-scrollbar { width: 4px; }
        .crm-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

        /* Name field */
        .crm-name-wrap { display: flex; flex-direction: column; gap: 6px; }
        .crm-label { font-size: 12.5px; font-weight: 700; color: #374151; }
        .crm-req { color: #dc2626; }
        .crm-input-icon-wrap { position: relative; }
        .crm-input-icon { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: #94a3b8; display: flex; align-items: center; pointer-events: none; }
        .crm-input {
          width: 100%; padding: 12px 14px 12px 40px;
          background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 11px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #0f172a;
          outline: none; transition: border-color .18s, box-shadow .18s;
          box-sizing: border-box;
        }
        .crm-input:focus { border-color: #e11d48; background: #fff; box-shadow: 0 0 0 3px rgba(225,29,72,.08); }
        .crm-input--err { border-color: #ef4444 !important; background: #fff7f7 !important; }
        .crm-input:disabled { background: #f1f5f9; color: #94a3b8; cursor: not-allowed; }
        .crm-err { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #dc2626; font-weight: 500; }

        /* Super admin wrap */
        .crm-super-wrap { }
        .crm-super-wrap .crm-perm-card { max-width: 320px; }

        /* Permission grid */
        .crm-perm-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 14px;
        }

        /* Permission card */
        .crm-perm-card {
          border-radius: 12px; overflow: hidden;
          border: 1.5px solid #e2e8f0;
          box-shadow: 0 1px 4px rgba(15,23,42,.05);
          transition: box-shadow .18s;
        }
        .crm-perm-card:hover { box-shadow: 0 4px 12px rgba(15,23,42,.08); }
        .crm-perm-header {
          display: flex; align-items: center; gap: 8px;
          padding: 10px 14px; border-bottom: 1.5px solid;
        }
        .crm-perm-title { font-size: 13px; font-weight: 800; flex: 1; }
        .crm-perm-count {
          font-size: 10.5px; font-weight: 700;
          padding: 2px 7px; border-radius: 20px;
        }
        .crm-perm-body {
          padding: 10px 14px; display: flex; flex-direction: column; gap: 8px;
          max-height: 220px; overflow-y: auto;
          background: #fff;
        }
        .crm-perm-body::-webkit-scrollbar { width: 3px; }
        .crm-perm-body::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 3px; }
        .crm-perm-row { display: flex; align-items: center; gap: 8px; cursor: pointer; }
        .crm-perm-label { font-size: 12.5px; font-weight: 500; color: #374151; line-height: 1.4; }

        /* Footer */
        .crm-footer {
          padding: 14px 26px; border-top: 1.5px solid #f1f5f9; background: #fafbfc;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 10px; flex-shrink: 0;
        }
        .crm-footer-hint { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #94a3b8; font-weight: 500; }
        .crm-footer-btns { display: flex; align-items: center; gap: 8px; }
        .crm-btn-cancel {
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; color: #64748b;
          padding: 9px 16px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff;
          cursor: pointer; transition: all .15s;
        }
        .crm-btn-cancel:hover { background: #f1f5f9; color: #374151; }
        .crm-btn-submit {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 20px; border-radius: 10px; border: none;
          background: linear-gradient(135deg,#be123c,#e11d48);
          cursor: pointer; box-shadow: 0 4px 14px rgba(225,29,72,.28);
          transition: transform .15s, box-shadow .18s; position: relative; overflow: hidden;
        }
        .crm-btn-submit::after { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,.1),transparent 60%); }
        .crm-btn-submit:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(225,29,72,.36); }
        .crm-btn-submit:disabled { opacity: .5; cursor: not-allowed; transform: none; }
        .crm-spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.35); border-top-color: #fff; border-radius: 50%; animation: crmSpin .65s linear infinite; }
        @keyframes crmSpin { to{transform:rotate(360deg)} }

        @media (max-width: 640px) {
          .crm-panel { max-width: 100%; }
          .crm-header { padding: 16px 18px; }
          .crm-body { padding: 18px; }
          .crm-footer { padding: 12px 18px; }
          .crm-perm-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </>
  );
};

export default CreateRoleMethod;